/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What this save is about to change somewhere else** (`P4` slice 2, N9d · #902 rule 2).
 *
 * The sentence #902 asks for, rendered before the write rather than discovered after it:
 *
 * > *"6 sources inherit this field. 5 will take this change. 1 has overridden it and won't — and 3
 * > of those 5 return to unproven."*
 *
 * ## Why it is a confirm and not a panel
 *
 * A panel is read by an operator who is looking for it, and the operator this exists for is the one
 * who does not know there is anything to look for — they are editing a source, not thinking about
 * the four variants somebody else built from it. An edit that reaches another artifact is
 * information they did not have, so it interrupts once.
 *
 * It interrupts **only** when the edit actually reaches something. A source with no children, or an
 * edit every child shields, saves with no dialog at all — otherwise the confirm becomes the thing
 * you click through, and the one that matters is lost with the rest.
 *
 * ## "Could not check" is its own answer
 *
 * Not a silent save, and not a blocked one. Blocking a save because a *preview* failed makes a
 * transient error look like a permission problem; saving silently tells the operator nothing is
 * affected on the strength of a question that failed. The dialog says which of the two it is and
 * lets them decide — the same rule `UsedByPanel` follows for its own failure state.
 */

import React from 'react';
import { Box, Text, VStack } from '@chakra-ui/react';

import { ConfirmDialog } from '../ui/ConfirmDialog';
import type { ArtifactEditImpact } from '../../types/artifact-usage';

/** Are there consequences worth interrupting for? The one place that decides. */
export function shouldConfirmEdit(impact: ArtifactEditImpact | null | undefined): boolean {
  if (!impact) return false;
  if (impact.unknown) return true;
  if (impact.hiddenCount > 0) return true;
  // A child that only *shields* is not a consequence — nothing about it changes. Interrupting for
  // one would train the operator to click through the confirm that matters.
  return impact.children.some((child) => child.takes.length > 0);
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export interface EditImpactDialogProps {
  impact: ArtifactEditImpact | null;
  /** Null when the preview itself failed — a different sentence from an empty impact. */
  failed?: boolean;
  artifactName?: string;
  /**
   * What one of these is called, singular and plural (`{ one: 'source', many: 'sources' }`). Required:
   * the dialog stands over every type with children since slice 7a, and a strategy's save warning that
   * said *sources* would describe somebody else's artifacts.
   */
  noun: { one: string; many: string };
  onConfirm: () => void;
  onCancel: () => void;
}

export const EditImpactDialog: React.FC<EditImpactDialogProps> = ({
  impact,
  failed,
  artifactName,
  noun,
  onConfirm,
  onCancel,
}) => {
  if (failed || !impact || impact.unknown) {
    return (
      <ConfirmDialog
        title="Save without checking what else this changes?"
        body={
          <Text fontSize="sm" color="fg.muted">
            bidBrain could not work out what saving {artifactName ?? `this ${noun.one}`} would change for the{' '}
            {noun.many} built from it. Saving is still safe for this {noun.one}; anything that inherits from it may
            change without warning.
          </Text>
        }
        confirmLabel="Save anyway"
        tone="danger"
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    );
  }

  const takers = impact.children.filter((child) => child.takes.length > 0);
  const shielders = impact.children.filter((child) => child.takes.length === 0 && child.shields.length > 0);
  const demoted = takers.filter((child) => child.returnsToUnproven);
  const affected = takers.length + impact.hiddenCount;

  return (
    <ConfirmDialog
      title={`This change reaches ${plural(affected, `other ${noun.one}`, `other ${noun.many}`)}`}
      body={
        <VStack align="stretch" gap={3}>
          {/* #902 rule 2's sentence, built from three counts rather than one: "6 inherit this. 5
              take it. 1 has overridden it and won't." Each clause is dropped when its count is
              zero, so the dialog never says "0 sources have overridden it" — a count of nothing is
              noise, and noise is what makes a warning stop being read. */}
          <Text fontSize="sm" color="fg.default">
            {plural(impact.children.length + impact.hiddenCount, `${noun.one} is`, `${noun.many} are`)} built from{' '}
            {artifactName ?? 'this one'}.
            {takers.length > 0 && ` ${plural(takers.length, 'takes', 'take')} this change.`}
            {shielders.length > 0 &&
              ` ${plural(shielders.length, 'has', 'have')} overridden it and will keep ${
                shielders.length === 1 ? 'its own value' : 'their own values'
              }.`}
          </Text>

          {takers.length > 0 && (
            <Box>
              {takers.map((child) => (
                <Text key={child.id} fontSize="xs" color="fg.muted">
                  <Text as="span" fontWeight="600" color="fg.default">
                    {child.name}
                  </Text>{' '}
                  takes {plural(child.takes.length, 'value', 'values')}
                  {child.shields.length > 0 && `, keeps ${plural(child.shields.length, 'of its own', 'of its own')}`}
                  {child.returnsToUnproven && ' — and returns to unproven'}
                </Text>
              ))}
            </Box>
          )}

          {demoted.length > 0 && (
            // Stated separately as well as per row, because it is the consequence that costs
            // something to undo: a proof is re-earned by running a test, not by editing back.
            <Text fontSize="sm" color="warning.ink">
              {plural(demoted.length, `${noun.one} loses`, `${noun.many} lose`)} a readiness proof and will need testing
              again.
            </Text>
          )}

          {impact.hiddenCount > 0 && (
            <Text fontSize="xs" color="fg.muted">
              {impact.hiddenCount === 1
                ? `One more ${noun.one} you do not have access to is also built from this one.`
                : `${impact.hiddenCount} more ${noun.many} you do not have access to are also built from this one.`}
            </Text>
          )}
        </VStack>
      }
      confirmLabel="Save"
      tone={demoted.length > 0 ? 'danger' : 'neutral'}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
};
